(function logoQualityCheckerModule() {
  const initializedCheckers = new WeakSet();
  const DPI_PERFECT = 300;
  const DPI_MIN = 150;
  const CM_PER_INCH = 2.54;
  const MAX_RENDER_EDGE = 1800;
  const BG_TOLERANCE = 30;
  const THIN_MM = 0.35;

  window.LogoQualityChecker = window.LogoQualityChecker || {};
  window.LogoQualityChecker.init = initChecker;
  window.LogoQualityChecker.bindLaunchers = bindLogoQualityCheckerLaunchers;

  document.addEventListener('DOMContentLoaded', () => {
    bindLogoQualityCheckerLaunchers();
  });
  bindLogoQualityCheckerLaunchers();

  function bindLogoQualityCheckerLaunchers() {
    document.querySelectorAll('[data-logo-quality-checker]').forEach((root) => initChecker(root));
    document.querySelectorAll('[data-logo-quality-checker-launcher]').forEach((button) => {
      if (button.dataset.logoQualityCheckerBound === 'true') return;
      button.dataset.logoQualityCheckerBound = 'true';
      button.addEventListener('click', () => openLogoQualityChecker(button));
    });
  }

  function openLogoQualityChecker(button) {
    if (button.dataset.customerLoggedIn !== 'true') {
      redirectLogoQualityCheckerLogin(button);
      return;
    }

    const template = document.querySelector(button.dataset.logoQualityCheckerTemplate);
    const mount = document.querySelector(button.dataset.logoQualityCheckerMount);
    if (!template || !mount) return;

    let checkerRoot = mount.querySelector('[data-logo-quality-checker]');
    if (!checkerRoot) {
      mount.textContent = '';
      mount.appendChild(template.content.cloneNode(true));
      checkerRoot = mount.querySelector('[data-logo-quality-checker]');
    }

    if (!checkerRoot) return;
    checkerRoot.__logoQualityCheckerLauncher = button;
    button.setAttribute('aria-expanded', 'true');
    document.body.classList.add('lqc-checker-open');
    initChecker(checkerRoot);
  }

  function redirectLogoQualityCheckerLogin(button) {
    const loginUrl = button.dataset.loginUrl || '/account/login';
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
    const separator = loginUrl.includes('?') ? '&' : '?';
    window.location.assign(`${loginUrl}${separator}return_url=${returnUrl}`);
  }

  function initChecker(root) {
    if (!root || initializedCheckers.has(root) || root.dataset.lqcReady === 'true') return;
    initializedCheckers.add(root);
    root.dataset.lqcReady = 'true';

    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = root.dataset.pdfWorkerUrl || '';
    }

    const $ = (selector) => root.querySelector(selector);
    const refs = {
      input: $('[data-file-input]'),
      dropzone: $('[data-dropzone]'),
      fileCard: $('[data-file-card]'),
      fileName: $('[data-file-name]'),
      fileMeta: $('[data-file-meta]'),
      thumbCanvas: $('[data-thumb-canvas]'),
      previewCanvas: $('[data-preview-canvas]'),
      previewShell: $('[data-preview-shell]'),
      preview: $('[data-preview]'),
      zoneOverlay: $('[data-zone-overlay]'),
      zoneToggles: $('[data-zone-toggles]'),
      zoneToggleThin: $('[data-zone-toggle="thin"]'),
      zoneToggleSemi: $('[data-zone-toggle="semi"]'),
      zoneLayerThin: $('[data-zone-layer="thin"]'),
      zoneLayerSemi: $('[data-zone-layer="semi"]'),
      retryMaestro: $('[data-action="retry-maestro"]'),
      diagnosticSection: $('[data-diagnostic-section]'),
      qualityBusy: $('[data-quality-busy]'),
      scoreSpinner: $('[data-score-spinner]'),
      emptyState: $('[data-empty-state]'),
      printWidth: $('[data-print-width]'),
      printHeight: $('[data-print-height]'),
      resetSize: $('[data-action="reset-size"]'),
      reset: $('[data-action="reset"]'),
      report: $('[data-action="download-report"]'),
      close: $('[data-action="close-checker"]'),
      scorePill: $('[data-score-pill]'),
      scoreLabel: $('[data-score-label]'),
      scoreValue: $('[data-score-value]'),
      overall: $('[data-overall-label]'),
      actions: $('[data-actions]'),
      actionCount: $('[data-action-count]'),
      creditPill: $('[data-credit-pill]'),
      creditLabel: $('[data-credit-label]'),
      creditCount: $('[data-credit-count]'),
      creditModal: $('[data-credit-modal]'),
      creditModalTitle: $('[data-credit-modal-title]'),
      creditModalText: $('[data-credit-modal-text]'),
      creditModalBalance: $('[data-credit-modal-balance]'),
      creditModalConfirm: $('[data-credit-confirm]'),
      creditModalCancel: $('[data-credit-cancel]'),
      toast: $('[data-toast]'),
    };

    document.body.classList.add('lqc-checker-open');

    const metricEls = {};
    root.querySelectorAll('[data-metric]').forEach((el) => {
      const key = el.dataset.metric;
      const toggle = el.querySelector('[data-metric-toggle]');
      const detail = el.querySelector('[data-metric-inline-detail]');
      metricEls[el.dataset.metric] = {
        item: el,
        toggle,
        value: root.querySelector(`[data-value="${key}"]`),
        detail,
        detailText: el.querySelector('[data-metric-inline-text]'),
      };
      if (toggle) toggle.addEventListener('click', () => toggleMetricDetail(key));
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const previewCtx = refs.previewCanvas.getContext('2d');
    const thumbCtx = refs.thumbCanvas.getContext('2d');

    const state = {
      file: null,
      fileKind: '',
      isVector: false,
      originalWidth: 0,
      originalHeight: 0,
      pageWidthCm: 0,
      pageHeightCm: 0,
      renderScale: 1,
      printWidthCm: 0,
      printHeightCm: 0,
      sourceRatio: 1,
      imageReady: false,
      analysis: null,
      report: null,
      creditsBalance: parseCreditBalance(root.dataset.creditBalance),
      creditsReady: root.dataset.creditRequired !== 'true' || parseCreditBalance(root.dataset.creditBalance) !== null,
      creditsLoading: false,
      creditsConsuming: false,
      freeDownloadUsed: false,
      maestroFilename: '',
      maestroValidations: null,
      maestroValidating: false,
      maestroError: '',
      maestroCache: {},
    };

    const serviceUrls = {
      editor: root.dataset.editorUrl || '/pages/editeur-en-ligne-de-logo',
      vectorAi: root.dataset.vectorAiUrl || '/products/vectorisation-automatique',
      vectorGraphic: root.dataset.vectorGraphicUrl || '/products/vectorisation-de-logo',
      removeBg: root.dataset.removeBgUrl || '/products/suppression-de-fond-en-intelligence-artificielle',
    };

    const creditConfig = {
      required: root.dataset.creditRequired === 'true',
      apiBase: root.dataset.creditApiBase || 'https://innovative-stack-i1z8l.ampt.app/api/v1',
      /* Même ledger que l'éditeur : les jetons PMD+ n'y sont pas forcément
         synchronisés dans le métachamp Shopify au moment du clic. */
      ledgerBase: root.dataset.creditLedgerBase || root.dataset.creditApiBase || 'https://innovative-stack-i1z8l.ampt.app/api/v1',
      buyUrl: root.dataset.creditBuyUrl || 'https://www.sherpa-transfert.com/products/credits-supplementaires-printmydtf',
      customerId: root.dataset.customerId || '',
      isLoggedIn: root.dataset.customerLoggedIn === 'true',
      metafieldNamespace: root.dataset.creditMetafieldNamespace || 'custom',
      metafieldKey: root.dataset.creditMetafieldKey || 'sub_usage',
      freeDownloadEnabled: root.dataset.freeDownloadEnabled !== 'false',
      freeDownloadUsed: parseFlag(root.dataset.freeDownloadUsed),
      freeDownloadStoragePrefix: root.dataset.freeDownloadStoragePrefix || 'sherpa-logo-quality-checker-report-free-download',
    };

    state.freeDownloadUsed = readFreeDownloadUsed();
    let creditModalResolve = null;
    let maestroRequestSeq = 0;
    let maestroDebounceTimer = 0;
    const MAESTRO_INFOS_URL = root.dataset.apiInfosUrl || '/apps/easy-order/file/infos';
    const MAESTRO_VALIDATE_URL = root.dataset.apiValidateUrl || '/apps/easy-order/file/validate';
    const MAESTRO_CHECK_KEYS = [
      'thickness',
      'mid_transparency',
      'transparency',
      'IMAGE_RESOLUTION_TROP_FAIBLE',
      'ESPACE_COULEUR_TON_DIRECT',
    ];

    refs.input.addEventListener('change', () => {
      const file = refs.input.files && refs.input.files[0];
      if (file) loadFile(file);
    });

    /* Le dépôt est accepté sur toute la fenêtre de l'outil, pas seulement sur la
       carte d'import : le centre du stage sert aussi de zone de dépôt. */
    ['dragenter', 'dragover'].forEach((eventName) => {
      root.addEventListener(eventName, (event) => {
        if (!event.dataTransfer?.types?.includes('Files')) return;
        event.preventDefault();
        setImportDragging(true);
      });
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      root.addEventListener(eventName, () => setImportDragging(false));
    });

    root.addEventListener('drop', (event) => {
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (!file) return;
      event.preventDefault();
      loadFile(file);
    });

    if (refs.emptyState) {
      refs.emptyState.addEventListener('click', () => refs.input.click());
    }

    refs.printWidth.addEventListener('input', () => setPrintWidth(Number(refs.printWidth.value)));
    refs.printHeight.addEventListener('input', () => setPrintHeight(Number(refs.printHeight.value)));
    refs.printWidth.addEventListener('change', () => setPrintWidth(Number(refs.printWidth.value)));
    refs.printHeight.addEventListener('change', () => setPrintHeight(Number(refs.printHeight.value)));
    refs.printWidth.addEventListener('blur', () => syncSizeInputs());
    refs.printHeight.addEventListener('blur', () => syncSizeInputs());
    if (refs.resetSize) refs.resetSize.addEventListener('click', resetPrintSize);
    refs.reset.addEventListener('click', reset);
    refs.report.addEventListener('click', downloadReport);
    if (refs.close) refs.close.addEventListener('click', closeChecker);
    refs.creditModalConfirm?.addEventListener('click', () => closeCreditModal(true));
    refs.creditModalCancel?.addEventListener('click', () => closeCreditModal(false));
    refs.retryMaestro?.addEventListener('click', () => { void refreshMaestroValidation({ force: true }); });
    refs.zoneLayerThin?.addEventListener('change', () => {
      if (refs.zoneLayerThin.checked) setZoneOverlay('thin');
      else if (!refs.zoneLayerSemi?.checked) clearZoneOverlay();
    });
    refs.zoneLayerSemi?.addEventListener('change', () => {
      if (refs.zoneLayerSemi.checked) setZoneOverlay('semi');
      else if (!refs.zoneLayerThin?.checked) clearZoneOverlay();
    });
    refs.creditModal?.addEventListener('click', (event) => {
      if (event.target === refs.creditModal) closeCreditModal(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && refs.creditModal && !refs.creditModal.hidden) closeCreditModal(false);
    });

    reset();
    refreshCredits();

    async function refreshCredits() {
      if (!creditConfig.required) {
        updateCreditUI();
        return null;
      }

      if (!creditConfig.isLoggedIn || !creditConfig.customerId) {
        state.creditsReady = true;
        updateCreditUI();
        return null;
      }

      state.creditsLoading = true;
      updateCreditUI();

      const metafieldBalance = parseCreditBalance(root.dataset.creditBalance);
      try {
        const response = await fetch(`${creditConfig.ledgerBase}/credit/balance?customer_id=${encodeURIComponent(creditConfig.customerId)}`);
        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          const ledgerBalance = parseCreditBalance(data.balance ?? data);
          if (ledgerBalance !== null) state.creditsBalance = ledgerBalance;
          else if (metafieldBalance !== null) state.creditsBalance = metafieldBalance;
        } else if (state.creditsBalance === null) {
          state.creditsBalance = metafieldBalance ?? 0;
        }
      } catch (error) {
        console.warn('Credit balance failed:', error);
        if (state.creditsBalance === null) state.creditsBalance = metafieldBalance ?? 0;
      }

      if (state.creditsBalance === null) state.creditsBalance = 0;
      state.creditsReady = true;
      state.creditsLoading = false;
      root.dataset.creditBalance = String(state.creditsBalance);
      updateCreditUI();
      return state.creditsBalance;
    }

    async function confirmDownloadCredit(formatLabel) {
      if (!creditConfig.required) return true;

      if (!creditConfig.isLoggedIn || !creditConfig.customerId) {
        redirectToLogin();
        return false;
      }

      await refreshCredits();

      if (!state.creditsReady) {
        showToast('Vérification des crédits impossible');
        return false;
      }

      const balance = Number(state.creditsBalance) || 0;
      const freeDownloadAvailable = hasFreeDownloadAvailable();
      if (balance <= 0 && !freeDownloadAvailable) {
        redirectToBuyCredits();
        return false;
      }

      if (!refs.creditModal) {
        return window.confirm(
          freeDownloadAvailable
            ? 'Votre premier téléchargement est offert. Aucun jeton IA ne sera consommé. Après cet essai gratuit, des jetons IA seront nécessaires pour télécharger.'
            : 'Ce téléchargement va consommer 1 jeton IA.'
        );
      }

      refs.creditModalTitle.textContent = `Télécharger le ${formatLabel}`;
      refs.creditModalText.textContent = freeDownloadAvailable
        ? 'Votre premier téléchargement est offert. Aucun jeton IA ne sera consommé.'
        : 'Ce téléchargement va consommer 1 jeton IA.';
      refs.creditModalBalance.textContent = freeDownloadAvailable
        ? 'Après cet essai gratuit, des jetons IA seront nécessaires pour télécharger vos prochains rapports.'
        : `Jetons IA disponibles : ${balance}. Après téléchargement : ${Math.max(0, balance - 1)}.`;
      if (refs.creditModalConfirm) {
        refs.creditModalConfirm.textContent = freeDownloadAvailable
          ? 'Télécharger gratuitement'
          : 'Télécharger et utiliser 1 jeton IA';
      }
      refs.creditModal.hidden = false;
      document.body.classList.add('lqc-credit-modal-open');
      refs.creditModalConfirm?.focus();

      return new Promise((resolve) => {
        creditModalResolve = resolve;
      });
    }

    async function consumeDownloadCredit() {
      if (!creditConfig.required) return true;

      if (!creditConfig.isLoggedIn || !creditConfig.customerId) {
        redirectToLogin();
        return false;
      }

      if (hasFreeDownloadAvailable()) {
        markFreeDownloadUsed();
        showToast('Téléchargement test gratuit utilisé');
        updateCreditUI();
        updateReportButton();
        return true;
      }

      if (!state.creditsReady) await refreshCredits();

      if (!state.creditsReady) {
        showToast('Vérification des crédits impossible');
        return false;
      }

      if (Number(state.creditsBalance) <= 0) {
        redirectToBuyCredits();
        return false;
      }

      const beforeBalance = Number(state.creditsBalance);
      state.creditsConsuming = true;
      updateCreditUI();
      updateReportButton();

      try {
        const response = await fetch(`${creditConfig.ledgerBase}/credit/use`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer_id: creditConfig.customerId }),
        });

        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) redirectToBuyCredits();
          else showToast('Vérification des crédits impossible');
          return false;
        }

        const data = await response.json().catch(() => ({}));
        const responseBalance = parseCreditBalance(data.balance ?? data);
        const fallbackBalance = Math.max(0, beforeBalance - 1);
        state.creditsBalance = responseBalance !== null ? responseBalance : fallbackBalance;
        state.creditsReady = true;
        root.dataset.creditBalance = String(state.creditsBalance);
        updateCreditUI();
        showToast('1 jeton IA consommé');
        return true;
      } catch (error) {
        console.warn('Credit use failed:', error);
        showToast('Vérification des crédits impossible');
        return false;
      } finally {
        state.creditsConsuming = false;
        updateCreditUI();
        updateReportButton();
      }
    }

    function closeCreditModal(confirmed) {
      if (refs.creditModal) refs.creditModal.hidden = true;
      document.body.classList.remove('lqc-credit-modal-open');
      if (creditModalResolve) {
        creditModalResolve(Boolean(confirmed));
        creditModalResolve = null;
      }
    }

    function updateCreditUI() {
      if (!refs.creditPill) return;

      if (!creditConfig.required) {
        refs.creditPill.hidden = true;
        return;
      }

      const balance = Math.max(0, Number(state.creditsBalance) || 0);
      const freeDownloadAvailable = hasFreeDownloadAvailable();

      refs.creditPill.hidden = false;
      refs.creditPill.classList.toggle('is-loading', state.creditsLoading || state.creditsConsuming);
      refs.creditPill.classList.toggle('is-empty', state.creditsReady && !freeDownloadAvailable && balance <= 0);
      refs.creditPill.classList.toggle('is-good', freeDownloadAvailable || (state.creditsReady && balance > 0));

      if (!creditConfig.isLoggedIn) {
        refs.creditLabel.textContent = 'Connexion requise';
        refs.creditCount.textContent = '-';
        return;
      }

      if (state.creditsLoading && !state.creditsReady) {
        refs.creditLabel.textContent = 'Crédits';
        refs.creditCount.textContent = '...';
        return;
      }

      if (!state.creditsReady) {
        refs.creditLabel.textContent = 'Crédits';
        refs.creditCount.textContent = '?';
        return;
      }

      if (freeDownloadAvailable) {
        refs.creditLabel.textContent = 'Essai gratuit';
        refs.creditCount.textContent = '1';
        return;
      }

      refs.creditLabel.textContent = balance > 1 ? 'Crédits' : 'Crédit';
      refs.creditCount.textContent = String(balance);
    }

    function updateReportButton() {
      if (refs.report) refs.report.disabled = !state.report || !hasMaestroResult() || state.creditsConsuming;
    }

    function redirectToBuyCredits() {
      showToast("Vous n'avez plus de crédits");
      window.setTimeout(() => {
        window.location.assign(creditConfig.buyUrl);
      }, 450);
    }

    function redirectToLogin() {
      showToast('Connectez-vous pour télécharger');
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
      window.setTimeout(() => {
        window.location.assign(`/account/login?return_url=${returnUrl}`);
      }, 450);
    }

    // Recharger la page viderait le logo importé juste après le téléchargement :
    // le solde est déjà décrémenté en place, il suffit de le resynchroniser.
    function syncCreditsAfterDownload() {
      if (!creditConfig.required) return;
      void refreshCredits();
    }

    function hasFreeDownloadAvailable() {
      return (
        creditConfig.freeDownloadEnabled &&
        creditConfig.isLoggedIn &&
        Boolean(creditConfig.customerId) &&
        !state.freeDownloadUsed
      );
    }

    function readFreeDownloadUsed() {
      if (!creditConfig.freeDownloadEnabled || !creditConfig.customerId) return false;
      if (creditConfig.freeDownloadUsed) return true;
      try {
        return window.localStorage.getItem(freeDownloadStorageKey()) === 'true';
      } catch (error) {
        return false;
      }
    }

    function markFreeDownloadUsed() {
      state.freeDownloadUsed = true;
      root.dataset.freeDownloadUsed = 'true';
      try {
        window.localStorage.setItem(freeDownloadStorageKey(), 'true');
      } catch (error) {
        console.warn('Free download flag failed:', error);
      }
    }

    function freeDownloadStorageKey() {
      return `${creditConfig.freeDownloadStoragePrefix}:${creditConfig.customerId}`;
    }

    function isMaestroInvalid(check) {
      return Boolean(check && typeof check === 'object' && check.is_valid === false);
    }

    function hasMaestroResult() {
      return Boolean(state.maestroValidations && typeof state.maestroValidations === 'object');
    }

    function maestroAlerts(validations) {
      const v = validations || state.maestroValidations || {};
      return {
        resolution: isMaestroInvalid(v.IMAGE_RESOLUTION_TROP_FAIBLE),
        thin: isMaestroInvalid(v.thickness),
        semi: isMaestroInvalid(v.mid_transparency),
        background: isMaestroInvalid(v.transparency),
        colors: isMaestroInvalid(v.ESPACE_COULEUR_TON_DIRECT),
      };
    }

    function resetMaestroState() {
      maestroRequestSeq += 1;
      window.clearTimeout(maestroDebounceTimer);
      state.maestroFilename = '';
      state.maestroValidations = null;
      state.maestroValidating = false;
      state.maestroError = '';
      state.maestroCache = {};
      clearZoneOverlay();
    }

    function looksLikeMaestroValidations(value) {
      return Boolean(
        value
        && typeof value === 'object'
        && !Array.isArray(value)
        && MAESTRO_CHECK_KEYS.some((key) => value[key] && typeof value[key] === 'object')
      );
    }

    function isHtmlPayload(value) {
      return /<!doctype html|<html[\s>]|has-header-tabs/i.test(String(value || ''));
    }

    function isInternalMaestroUrl(url) {
      return /maestro-cs-public-api|localhost|127\.0\.0\.1|\.internal[:/]/i.test(String(url || ''));
    }

    function scoreMaestroUrl(url) {
      const value = String(url || '');
      if (!value || isInternalMaestroUrl(value) || isHtmlPayload(value)) return 0;
      if (/api\.cloud\.sherpa-transfert\.app\/api\/file\/get\//i.test(value)) return 6;
      if (/s3\.gra\.io\.cloud\.ovh\.net\/customer-library\//i.test(value) && /X-Amz-/i.test(value)) return 5;
      if (/upload\.cloudlift\.app\/s\//i.test(value)) return 4;
      if (/^https:\/\/s3\./i.test(value) || /storage\.googleapis\.com/i.test(value)) return 3;
      if (/^https:\/\//i.test(value)) return 2;
      if (/^http:\/\//i.test(value)) return 1;
      return 0;
    }

    function isPublicMaestroUrl(url) {
      return scoreMaestroUrl(url) >= 2;
    }

    function isStableMaestroUrl(url) {
      if (!isPublicMaestroUrl(url)) return false;
      try {
        return !new URL(String(url)).search;
      } catch (_) {
        return false;
      }
    }

    function pickMaestroUrl(...candidates) {
      const urls = candidates.map((value) => String(value || '').trim()).filter(Boolean);
      return urls.find((url) => isStableMaestroUrl(url)) || urls.find((url) => isPublicMaestroUrl(url)) || '';
    }

    function sleep(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }

    function maestroUserError(error) {
      const raw = String(error?.message || error || '');
      if (isHtmlPayload(raw)) return 'Erreur serveur lors de la vérification.';
      return raw || 'Vérification impossible';
    }

    async function maestroFetch(url, options = {}) {
      const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
        ...options,
        headers: { Accept: 'application/json', ...(options.headers || {}) },
      });
      return parseProxyJson(response);
    }

    async function parseProxyJson(response) {
      const text = await response.text();
      if (isHtmlPayload(text)) {
        const error = new Error('Erreur serveur lors de la vérification.');
        error.status = response.status || 500;
        throw error;
      }
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch (_) { json = null; }
      if (!response.ok) {
        const error = new Error(
          json?.message || json?.error || json?.STATUS_DESCRIPTION || `HTTP ${response.status}`
        );
        error.status = response.status;
        throw error;
      }
      if (json && Number(json.STATUS_CODE) && Number(json.STATUS_CODE) !== 200) {
        throw new Error(json.STATUS_DESCRIPTION || json.message || 'Erreur Maestro');
      }
      return json;
    }

    function sanitizeUploadFilename(name, fallbackExt = 'png') {
      const raw = String(name || '');
      const dot = raw.lastIndexOf('.');
      const toAscii = (value) => value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9._-]+/g, '-');
      const stem = toAscii(dot > 0 ? raw.slice(0, dot) : raw).replace(/^-+|-+$/g, '').slice(0, 80) || 'logo';
      const ext = toAscii(dot > 0 ? raw.slice(dot + 1) : '').toLowerCase() || fallbackExt;
      return `${stem}.${ext}`;
    }

    function canvasToPngBlob() {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) reject(new Error('Export PNG impossible'));
          else resolve(blob);
        }, 'image/png');
      });
    }

    async function fileForMaestroStorage(file) {
      const ext = String(file.name || '').split('.').pop().toLowerCase();
      if (['png', 'pdf', 'ai'].includes(ext)) {
        const safeName = sanitizeUploadFilename(file.name, ext);
        if (safeName === file.name) return file;
        return new File([file], safeName, { type: file.type || 'application/octet-stream' });
      }
      const blob = await canvasToPngBlob();
      const base = sanitizeUploadFilename(file.name, 'png').replace(/\.[^.]+$/, '');
      return new File([blob], `${base}.png`, { type: 'image/png' });
    }

    function libraryFilesFrom(json) {
      if (Array.isArray(json?.files)) return json.files;
      if (Array.isArray(json?.data?.files)) return json.data.files;
      return [];
    }

    function downloadUrlFromLibraryFile(file) {
      const url = String(file?.download_url || '').trim();
      return isPublicMaestroUrl(url) ? url : '';
    }

    async function listLibraryFiles() {
      const listPath = root.dataset.libraryListUrl || '/apps/easy-order/file-storage/list';
      const url = new URL(listPath, window.location.origin);
      url.searchParams.set('user_id', creditConfig.customerId);
      return libraryFilesFrom(await maestroFetch(url.toString(), { method: 'GET' }));
    }

    async function waitForLibraryDownloadUrl(key, preferredName = '') {
      const needle = String(preferredName || '').toLowerCase();
      for (let attempt = 0; attempt < 6; attempt += 1) {
        const files = await listLibraryFiles();
        const byKey = key
          ? files.find((file) => String(file?.key || '') === key)
          : null;
        const sorted = [...files].sort(
          (a, b) => new Date(b?.last_modified || 0) - new Date(a?.last_modified || 0)
        );
        const byName = needle
          ? sorted.find((file) => {
            const hay = [
              file?.filename,
              file?.name,
              file?.key,
              file?.metadata?.original_filename,
            ].map((value) => String(value || '').toLowerCase()).join(' ');
            return hay.includes(needle);
          })
          : null;
        const url = downloadUrlFromLibraryFile(byKey)
          || downloadUrlFromLibraryFile(byName)
          || downloadUrlFromLibraryFile(sorted[0]);
        if (url) return url;
        await sleep(400);
      }
      return '';
    }

    async function uploadMaestroFileViaStorage(file) {
      if (!creditConfig.customerId) {
        throw new Error('Connectez-vous pour vérifier le fichier.');
      }
      const uploadFile = await fileForMaestroStorage(file);
      const uploadPath = root.dataset.libraryUploadUrl || '/apps/easy-order/file-storage/upload';
      const url = new URL(uploadPath, window.location.origin);
      url.searchParams.set('user_id', creditConfig.customerId);
      const formData = new FormData();
      formData.append('file', uploadFile, uploadFile.name || 'logo.png');
      const widthMm = Math.max(1, Math.round((state.printWidthCm || 0) * 10));
      const heightMm = Math.max(1, Math.round((state.printHeightCm || 0) * 10));
      if (widthMm > 0) formData.append('width_mm', String(widthMm));
      if (heightMm > 0) formData.append('height_mm', String(heightMm));

      const uploaded = await maestroFetch(url.toString(), {
        method: 'POST',
        body: formData,
      });
      const key = String(uploaded?.key || '').trim();
      const listed = await waitForLibraryDownloadUrl(key, uploadFile.name);
      if (!listed) {
        throw new Error('L’upload n’a pas renvoyé d’URL publique de vérification.');
      }
      return listed;
    }

    async function ensureMaestroFilename() {
      if (isPublicMaestroUrl(state.maestroFilename)) return state.maestroFilename;
      if (!state.file) throw new Error('Aucun fichier à vérifier.');
      const storageUrl = await uploadMaestroFileViaStorage(state.file);
      state.maestroFilename = storageUrl;
      return storageUrl;
    }

    async function resolveMaestroFilename(sourceUrl) {
      if (!sourceUrl) return '';
      try {
        const infosUrl = new URL(MAESTRO_INFOS_URL, window.location.origin);
        infosUrl.searchParams.set('filename', sourceUrl);
        infosUrl.searchParams.set('crop_transparent_area', 'false');
        const json = await maestroFetch(infosUrl.toString(), { method: 'GET' });
        return pickMaestroUrl(
          json?.FILE_CROPPED,
          json?.FILE_PNG_FULL_SIZE,
          json?.FILE_URL,
          sourceUrl
        );
      } catch (error) {
        console.warn('Maestro file/infos failed, validating source URL', error);
        return isPublicMaestroUrl(sourceUrl) ? sourceUrl : '';
      }
    }

    function normalizeMaestroValidations(json) {
      if (!json || typeof json !== 'object') return null;
      const candidates = [
        json.validations,
        json.data?.validations,
        json.result?.validations,
        json.data,
        json.result,
        json,
      ];
      for (const candidate of candidates) {
        if (looksLikeMaestroValidations(candidate)) {
          return { ...candidate };
        }
      }
      const fallback = json.validations || json.data || json.result || json;
      return fallback && typeof fallback === 'object' && !Array.isArray(fallback) ? { ...fallback } : null;
    }

    async function fetchMaestroValidations(filename, widthMm, heightMm, options = {}) {
      const cacheKey = `${filename}::${Math.round(widthMm)}x${Math.round(heightMm)}`;
      if (!options.skipCache && state.maestroCache[cacheKey]) return state.maestroCache[cacheKey];
      const url = new URL(MAESTRO_VALIDATE_URL, window.location.origin);
      url.searchParams.set('filename', filename);
      url.searchParams.set('width', String(Math.max(1, Math.round(widthMm))));
      url.searchParams.set('height', String(Math.max(1, Math.round(heightMm))));
      url.searchParams.set('method', 'logo');
      const json = await maestroFetch(url.toString(), { method: 'GET' });
      const payload = normalizeMaestroValidations(json);
      if (!payload) throw new Error('Réponse de validation invalide');
      if (payload.ESPACE_COULEUR_HORS_CMJN) delete payload.ESPACE_COULEUR_HORS_CMJN;
      state.maestroCache[cacheKey] = payload;
      return payload;
    }

    async function fetchMaestroValidationsWithRetry(filename, widthMm, heightMm, options = {}) {
      let lastError = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          return await fetchMaestroValidations(filename, widthMm, heightMm, options);
        } catch (error) {
          lastError = error;
          const status = Number(error?.status || 0);
          const retryable = status === 0 || status === 500 || status === 502 || status === 503 || status === 504 || status === -1;
          if (!retryable || attempt === 2) throw error;
          await new Promise((resolve) => window.setTimeout(resolve, 4000));
        }
      }
      throw lastError || new Error('Vérification impossible');
    }

    function maestroAssetUrl(value) {
      const url = String(value || '').trim();
      if (!url || isHtmlPayload(url)) return '';
      return url.replace(/^http:\/\/api\.cloud\.sherpa-transfert\.app/i, 'https://api.cloud.sherpa-transfert.app');
    }

    function maestroCheckPreview(check) {
      if (!check || typeof check !== 'object') return '';
      return maestroAssetUrl(check.summary_file || check.preview_file || check.mask_file);
    }

    function maestroProblemZones() {
      if (!hasMaestroResult()) return [];
      const v = state.maestroValidations || {};
      return [
        { key: 'thin', label: 'Zones fines', check: v.thickness },
        { key: 'semi', label: 'Semi-transparence', check: v.mid_transparency },
      ].reduce((list, item) => {
        if (!isMaestroInvalid(item.check)) return list;
        const url = maestroCheckPreview(item.check);
        if (!url) return list;
        list.push({ key: item.key, label: item.label, url });
        return list;
      }, []);
    }

    function maestroZoneByKey(key) {
      return maestroProblemZones().find((zone) => zone.key === key) || null;
    }

    /* `null` = contrôle non remonté par la vérification. Le rapport omet alors la
       ligne au lieu de retomber sur l'analyse locale, qui ne fait pas foi. */
    function maestroCheckVerdict(check) {
      if (!check || typeof check !== 'object') return null;
      const raw = check.is_valid;
      if (raw === true || raw === 'true' || raw === 1) return 'Non';
      if (raw === false || raw === 'false' || raw === 0) return 'Oui';
      return null;
    }

    function maestroZoneLine(label, check) {
      const verdict = maestroCheckVerdict(check);
      return verdict ? [[label, verdict]] : [];
    }

    function clearZoneOverlay() {
      if (refs.zoneLayerThin) refs.zoneLayerThin.checked = false;
      if (refs.zoneLayerSemi) refs.zoneLayerSemi.checked = false;
      if (refs.zoneOverlay) {
        refs.zoneOverlay.hidden = true;
        refs.zoneOverlay.removeAttribute('src');
        refs.zoneOverlay.alt = '';
      }
    }

    function setZoneOverlay(key) {
      const zone = maestroZoneByKey(key);
      if (!refs.zoneOverlay || !zone) {
        clearZoneOverlay();
        return;
      }
      if (refs.zoneLayerThin) refs.zoneLayerThin.checked = key === 'thin';
      if (refs.zoneLayerSemi) refs.zoneLayerSemi.checked = key === 'semi';
      refs.zoneOverlay.alt = zone.label;
      refs.zoneOverlay.src = zone.url;
      refs.zoneOverlay.hidden = false;
    }

    function updateZoneToggles() {
      const zones = maestroProblemZones();
      const thin = zones.find((zone) => zone.key === 'thin');
      const semi = zones.find((zone) => zone.key === 'semi');
      if (refs.zoneToggleThin) refs.zoneToggleThin.hidden = !thin;
      if (refs.zoneToggleSemi) refs.zoneToggleSemi.hidden = !semi;
      if (refs.zoneToggles) refs.zoneToggles.hidden = !thin && !semi;
      const active = refs.zoneLayerThin?.checked
        ? 'thin'
        : refs.zoneLayerSemi?.checked
          ? 'semi'
          : '';
      if (active && maestroZoneByKey(active)) setZoneOverlay(active);
      else if (!active) {
        if (refs.zoneOverlay) {
          refs.zoneOverlay.hidden = true;
          refs.zoneOverlay.removeAttribute('src');
          refs.zoneOverlay.alt = '';
        }
      } else {
        clearZoneOverlay();
      }
    }

    function applyMaestroToAnalysis(analysis) {
      analysis.maestroReady = hasMaestroResult();
      analysis.maestroPending = Boolean(state.maestroValidating && !hasMaestroResult());
      analysis.maestroError = state.maestroError || '';
      const alerts = hasMaestroResult() ? maestroAlerts() : null;
      analysis.maestroAlerts = alerts;
      if (!alerts) {
        analysis.hasSemiTransparency = false;
        analysis.thin = { thresholdMm: THIN_MM, ratio: 0, hasThinAreas: false };
        analysis.hasSpotColors = false;
        analysis.hasResolutionIssue = false;
        return analysis;
      }
      analysis.hasSemiTransparency = alerts.semi;
      analysis.semiRatio = alerts.semi ? Math.max(analysis.semiRatio || 0, 0.01) : 0;
      analysis.thin = {
        thresholdMm: THIN_MM,
        ratio: alerts.thin ? 0.01 : 0,
        hasThinAreas: alerts.thin,
      };
      analysis.hasBackground = alerts.background;
      analysis.backgroundStatus = alerts.background
        ? (analysis.backgroundStatus === 'none' ? 'solid' : analysis.backgroundStatus)
        : 'none';
      analysis.hasResolutionIssue = alerts.resolution;
      analysis.hasSpotColors = alerts.colors;
      if (!analysis.dpi) analysis.dpi = effectiveDpi();
      return analysis;
    }

    function scheduleMaestroRefresh() {
      window.clearTimeout(maestroDebounceTimer);
      maestroRequestSeq += 1;
      state.maestroValidating = true;
      state.maestroValidations = null;
      setQualityBusy(true);
      if (state.analysis) {
        applyMaestroToAnalysis(state.analysis);
        renderMetrics(state.analysis);
        renderActions(state.analysis);
      }
      maestroDebounceTimer = window.setTimeout(() => {
        void refreshMaestroValidation({ skipCache: true });
      }, 450);
    }

    async function refreshMaestroValidation(options = {}) {
      if (!state.file || !state.imageReady) return;
      const seq = (maestroRequestSeq += 1);
      const force = Boolean(options.force);
      const skipCache = force || Boolean(options.skipCache);
      if (force) {
        state.maestroCache = {};
        state.maestroFilename = '';
      }
      state.maestroValidating = true;
      state.maestroError = '';
      state.maestroValidations = null;
      setQualityBusy(true);
      if (state.analysis) applyMaestroToAnalysis(state.analysis);
      if (state.analysis) {
        renderMetrics(state.analysis);
        renderActions(state.analysis);
      }
      updateZoneToggles();
      try {
        const sourceUrl = await ensureMaestroFilename();
        if (seq !== maestroRequestSeq) return;
        const filename = await resolveMaestroFilename(sourceUrl);
        if (!filename) throw new Error('URL de vérification manquante.');
        state.maestroFilename = filename;
        if (seq !== maestroRequestSeq) return;
        const widthMm = Math.max(1, Math.round((state.printWidthCm || 0) * 10));
        const heightMm = Math.max(1, Math.round((state.printHeightCm || 0) * 10));
        console.info('[LQC maestro] validate', { widthMm, heightMm, skipCache });
        const validations = await fetchMaestroValidationsWithRetry(filename, widthMm, heightMm, { skipCache });
        if (seq !== maestroRequestSeq) return;
        state.maestroValidations = validations;
        state.maestroError = '';
      } catch (error) {
        if (seq !== maestroRequestSeq) return;
        console.warn('Maestro validate failed', error);
        state.maestroValidations = null;
        state.maestroError = maestroUserError(error);
        showToast(state.maestroError);
      } finally {
        if (seq === maestroRequestSeq) {
          state.maestroValidating = false;
          setQualityBusy(false);
          if (state.analysis) {
            applyMaestroToAnalysis(state.analysis);
            state.report = buildReport(state.analysis);
            renderMetrics(state.analysis);
            renderActions(state.analysis);
            updateReportButton();
          }
          updateZoneToggles();
        }
      }
    }

    async function loadFile(file) {
      setBusy(true);
      try {
        reset(false);
        resetMaestroState();
        state.file = file;
        const ext = extensionOf(file.name);
        state.fileKind = ext || file.type || 'fichier';

        if (ext === 'svg' || file.type === 'image/svg+xml') {
          await loadSvgFile(file);
        } else if (ext === 'pdf' || ext === 'ai' || file.type === 'application/pdf' || file.type === 'application/postscript' || file.type === 'application/illustrator') {
          await loadPdfLikeFile(file);
        } else if (file.type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
          await loadRasterFile(file);
        } else {
          throw new Error('Format non reconnu');
        }

        state.sourceRatio = state.originalHeight > 0 ? state.originalWidth / state.originalHeight : 1;
        if (!(state.printWidthCm > 0)) resetPrintSize(false);
        refs.printWidth.disabled = false;
        refs.printHeight.disabled = false;
        if (refs.resetSize) refs.resetSize.disabled = state.isVector;
        refs.reset.disabled = false;
        state.imageReady = true;
        analyzeAndRender();
        void refreshMaestroValidation();
        showToast('Logo analysé');
      } catch (error) {
        console.error(error);
        showToast(error.message || 'Analyse impossible');
        reset(false);
      } finally {
        setBusy(false);
      }
    }

    async function loadRasterFile(file) {
      state.isVector = false;
      const dataUrl = await readFileAsDataURL(file);
      const image = await loadImage(dataUrl);
      state.originalWidth = image.naturalWidth || image.width;
      state.originalHeight = image.naturalHeight || image.height;
      state.renderScale = drawToAnalysisCanvas(image, state.originalWidth, state.originalHeight);
      state.printWidthCm = pxToCm(state.originalWidth);
      state.printHeightCm = pxToCm(state.originalHeight);
      updateFileCard(`${state.originalWidth} x ${state.originalHeight} px`);
    }

    async function loadSvgFile(file) {
      state.isVector = true;
      const text = await file.text();
      const geometry = readSvgGeometry(text);
      const blobUrl = URL.createObjectURL(new Blob([text], { type: 'image/svg+xml' }));
      try {
        const image = await loadImage(blobUrl);
        state.originalWidth = Math.max(1, Math.round(geometry.width || image.naturalWidth || image.width || 1000));
        state.originalHeight = Math.max(1, Math.round(geometry.height || image.naturalHeight || image.height || 1000));
        state.renderScale = drawToAnalysisCanvas(image, state.originalWidth, state.originalHeight);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
      state.printWidthCm = geometry.widthCm || pxToCm(state.originalWidth);
      state.printHeightCm = geometry.heightCm || pxToCm(state.originalHeight);
      updateFileCard('SVG vectoriel');
    }

    async function loadPdfLikeFile(file) {
      state.isVector = true;
      if (!window.pdfjsLib) throw new Error('Lecteur PDF indisponible');
      const buffer = await file.arrayBuffer();
      const doc = await window.pdfjsLib.getDocument({ data: buffer }).promise;
      const page = await doc.getPage(1);
      const baseViewport = page.getViewport({ scale: 1 });
      state.pageWidthCm = ptToCm(baseViewport.width);
      state.pageHeightCm = ptToCm(baseViewport.height);
      state.originalWidth = Math.max(1, Math.round(baseViewport.width * (DPI_PERFECT / 72)));
      state.originalHeight = Math.max(1, Math.round(baseViewport.height * (DPI_PERFECT / 72)));
      const scale = Math.min(MAX_RENDER_EDGE / baseViewport.width, MAX_RENDER_EDGE / baseViewport.height, 2.5);
      const viewport = page.getViewport({ scale: Math.max(0.001, Math.min(scale, 2.5)) });
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      state.renderScale = canvas.width / state.originalWidth;
      await page.render({ canvasContext: ctx, viewport, background: 'rgba(0,0,0,0)' }).promise;
      state.printWidthCm = state.pageWidthCm || pxToCm(state.originalWidth);
      state.printHeightCm = state.pageHeightCm || pxToCm(state.originalHeight);
      updateFileCard('PDF/Illustrator vectoriel probable');
    }

    function drawToAnalysisCanvas(image, originalW, originalH) {
      const scale = Math.min(1, MAX_RENDER_EDGE / Math.max(originalW, originalH));
      canvas.width = Math.max(1, Math.round(originalW * scale));
      canvas.height = Math.max(1, Math.round(originalH * scale));
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      return scale;
    }

    function analyzeAndRender(preserveAxis = '') {
      if (!state.file || !canvas.width || !canvas.height) return;
      syncSizeInputs(preserveAxis);
      state.analysis = applyMaestroToAnalysis(analyzeImage());
      renderPreview(state.analysis);
      renderThumb(state.analysis);
      state.report = buildReport(state.analysis);
      renderMetrics(state.analysis);
      renderActions(state.analysis);
      updateReportButton();
      updateZoneToggles();
    }

    function analyzeImage() {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const w = imageData.width;
      const h = imageData.height;
      const total = w * h;
      const edge = analyzeEdges(data, w, h);
      const transparentBackground = edge.samples > 0 && edge.transparent / edge.samples > 0.08;
      const edgeOpaqueRatio = edge.samples > 0 ? edge.opaque / edge.samples : 0;
      const bg = edgeAverageColor(data, w, h);

      const mask = new Uint8Array(total);
      let transparentPixels = 0;
      let semiPixels = 0;
      let contentPixels = 0;
      let minX = w;
      let minY = h;
      let maxX = -1;
      let maxY = -1;
      const colorSet = new Set();
      const colorStep = Math.max(1, Math.floor(total / 30000));

      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const p = y * w + x;
          const idx = p * 4;
          const alpha = data[idx + 3];
          if (alpha < 8) transparentPixels += 1;
          if (alpha > 8 && alpha < 245) semiPixels += 1;
          const visible = transparentBackground
            ? alpha > 10
            : alpha > 10 && colorDistanceAt(data, idx, bg) > BG_TOLERANCE;
          if (!visible) continue;
          mask[p] = 1;
          contentPixels += 1;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          if (p % colorStep === 0) {
            colorSet.add(`${data[idx] >> 4}-${data[idx + 1] >> 4}-${data[idx + 2] >> 4}`);
          }
        }
      }

      const bounds = contentPixels
        ? { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 }
        : null;
      const boundsOriginal = bounds ? scaleRect(bounds, 1 / state.renderScale) : null;
      const whitespaceRatio = bounds ? clamp(1 - (bounds.w * bounds.h) / (w * h), 0, 1) : 1;
      const hasWhitespace = Boolean(bounds) && whitespaceRatio > 0.015
        && (bounds.x > 3 || bounds.y > 3 || bounds.x + bounds.w < w - 3 || bounds.y + bounds.h < h - 3);
      const dpi = effectiveDpi();
      const backgroundStatus = transparentBackground
        ? 'none'
        : edgeOpaqueRatio > 0.86
          ? 'solid'
          : 'partial';
      const complex = !state.isVector && (colorSet.size > 42 || contentPixels / total > 0.72);

      return {
        fileName: state.file.name,
        fileKind: state.fileKind,
        isVector: state.isVector,
        originalWidth: state.originalWidth,
        originalHeight: state.originalHeight,
        pageWidthCm: state.pageWidthCm,
        pageHeightCm: state.pageHeightCm,
        printWidthCm: state.printWidthCm,
        printHeightCm: state.printHeightCm,
        trimmedWidthCm: boundsOriginal ? pxToCurrentCm(boundsOriginal.w, 'w') : 0,
        trimmedHeightCm: boundsOriginal ? pxToCurrentCm(boundsOriginal.h, 'h') : 0,
        boundsCanvas: bounds,
        boundsOriginal,
        dpi,
        transparentRatio: transparentPixels / total,
        semiRatio: 0,
        hasSemiTransparency: false,
        backgroundStatus,
        hasBackground: backgroundStatus !== 'none',
        hasWhitespace,
        whitespaceRatio,
        hasContent: Boolean(bounds),
        thin: { thresholdMm: THIN_MM, ratio: 0, hasThinAreas: false },
        hasSpotColors: false,
        hasResolutionIssue: false,
        maestroReady: false,
        maestroPending: false,
        maestroError: '',
        maestroAlerts: null,
        complex,
      };
    }

    function renderMetrics(analysis) {
      const alerts = analysis.maestroAlerts;
      const maestroPending = Boolean(analysis.maestroPending);
      const maestroMissing = !alerts && !maestroPending;
      const maestroLabel = maestroPending
        ? 'Contrôle en cours'
        : maestroMissing
          ? (analysis.maestroError ? 'Échec' : 'Indisponible')
          : '';
      const maestroTone = maestroPending || maestroMissing ? 'empty' : 'good';
      const issues = collectIssues(analysis);
      const bad = issues.filter((item) => item.level === 'bad').length;
      const warn = issues.filter((item) => item.level === 'warn').length;
      const score = Math.max(0, 100 - bad * 28 - warn * 12);
      const tone = !alerts ? (maestroPending ? 'empty' : 'bad') : bad ? 'bad' : warn ? 'warn' : 'good';
      const label = maestroPending
        ? 'Contrôle en cours'
        : !alerts
          ? (analysis.maestroError || 'Vérification incomplète')
          : bad ? 'À corriger' : warn ? 'À améliorer' : 'Prêt pour impression';

      setQualityBusy(maestroPending);
      setScore(label, alerts ? `${score}/100` : '––/100', tone);
      refs.overall.textContent = label;
      if (refs.retryMaestro) refs.retryMaestro.hidden = maestroPending || !maestroMissing;
      setMetric('dimensions', formatSize(analysis.printWidthCm, analysis.printHeightCm), 'good');
      setMetric('trimmed', analysis.boundsOriginal ? formatSize(analysis.trimmedWidthCm, analysis.trimmedHeightCm) : 'Logo non détecté', analysis.boundsOriginal ? (analysis.hasWhitespace ? 'warn' : 'good') : 'bad');
      setMetricVisible('trimmed', shouldShowTrimmedMetric(analysis));
      setMetric('type', analysis.isVector ? 'Vectoriel' : 'Pixel', 'good');
      if (analysis.isVector && !analysis.hasResolutionIssue) {
        setMetric('dpi', 'Fichier vectoriel', 'good');
      } else {
        setMetric('dpi', `${analysis.dpi} DPI`, analysis.hasResolutionIssue ? dpiTone(analysis.dpi) : (alerts || analysis.isVector ? 'good' : dpiTone(analysis.dpi)));
      }
      if (!alerts) setMetric('semi', maestroLabel, maestroTone);
      else setMetric('semi', analysis.hasSemiTransparency ? 'Oui' : 'Non', analysis.hasSemiTransparency ? 'warn' : 'good');
      if (!alerts && maestroPending) setMetric('background', 'Contrôle en cours', 'empty');
      else if (!alerts && maestroMissing) setMetric('background', maestroLabel, maestroTone);
      else setMetric('background', analysis.hasBackground ? (alerts ? 'Fond détecté' : backgroundLabel(analysis.backgroundStatus)) : 'Non', analysis.hasBackground ? 'warn' : 'good');
      if (!alerts) setMetric('thin', maestroLabel, maestroTone);
      else setMetric('thin', analysis.thin.hasThinAreas ? 'Oui' : 'Non', analysis.thin.hasThinAreas ? 'warn' : 'good');
      if (!alerts) setMetric('colors', maestroLabel, maestroTone);
      else setMetric('colors', analysis.hasSpotColors ? 'Tons directs' : 'OK', analysis.hasSpotColors ? 'warn' : 'good');
    }

    function toggleMetricDetail(key) {
      if (!state.analysis) return;
      const metric = metricEls[key];
      if (!metric || metric.toggle.disabled) return;
      setMetricOpen(key, metric.item.getAttribute('aria-expanded') !== 'true');
    }

    function metricDetail(key, analysis) {
      const dpiText = `${analysis.dpi} DPI`;
      const details = {
        dimensions: {
          tone: 'good',
          title: 'Dimension d’impression',
          text: `Votre fichier fait ${formatSize(analysis.printWidthCm, analysis.printHeightCm)}. Si vous changez cette taille, la résolution calculée change aussi.`,
        },
        trimmed: {
          tone: analysis.boundsOriginal ? (analysis.hasWhitespace ? 'warn' : 'good') : 'bad',
          title: 'Zone réellement utile',
          text: analysis.boundsOriginal
            ? `Sans les espaces vides autour du logo, la zone utile mesure ${formatSize(analysis.trimmedWidthCm, analysis.trimmedHeightCm)}. Les espaces vides sont inutiles, les laisser risque de créer des erreurs.`
            : 'L’outil ne trouve pas de zone de logo exploitable dans le fichier.',
        },
        type: {
          tone: 'good',
          title: 'Type de fichier',
          text: analysis.isVector
            ? 'Le fichier semble vectoriel : il peut normalement être agrandi sans perdre de netteté.'
            : 'Le fichier est en pixels : sa netteté dépend de sa taille d’impression et de sa résolution.',
        },
        dpi: {
          tone: analysis.isVector && !analysis.hasResolutionIssue ? 'good' : (analysis.hasResolutionIssue ? dpiTone(analysis.dpi) : dpiTone(analysis.dpi)),
          title: 'Résolution à cette taille',
          text: analysis.isVector && !analysis.hasResolutionIssue
            ? 'Le fichier est vectoriel : il peut être agrandi sans perdre de netteté.'
            : analysis.hasResolutionIssue
              ? `À la taille choisie, la résolution est trop faible (${dpiText}). Sous 150 DPI c’est insuffisant, entre 150 et 300 DPI c’est utilisable mais pas parfait.`
              : `À la taille choisie, le fichier est à ${dpiText}. Sous 150 DPI c’est insuffisant, entre 150 et 300 DPI c’est utilisable mais pas parfait, et à partir de 300 DPI c’est idéal.`,
        },
        semi: {
          tone: analysis.maestroPending || !analysis.maestroAlerts ? 'empty' : (analysis.hasSemiTransparency ? 'warn' : 'good'),
          title: 'Semi-transparence',
          text: analysis.maestroPending
            ? 'Contrôle en cours via l’API de vérification.'
            : !analysis.maestroAlerts
              ? (analysis.maestroError || 'La vérification API n’a pas pu aboutir.')
              : analysis.hasSemiTransparency
                ? 'Des zones semi-transparentes ont été détectées. À l’impression, ces zones peuvent donner des bords flous ou imprévisibles.'
                : 'Aucune semi-transparence notable n’a été détectée.',
        },
        background: {
          tone: analysis.hasBackground ? 'warn' : 'good',
          title: 'Fond détecté',
          text: analysis.hasBackground
            ? `Fond détecté : ${analysis.maestroAlerts ? 'un fond non transparent a été signalé par la vérification.' : `${backgroundLabel(analysis.backgroundStatus)}. Un fond non voulu peut s’imprimer comme un rectangle autour du logo.`}`
            : 'Le logo semble être sur fond transparent, ce qui est préférable pour l’impression.',
        },
        thin: {
          tone: analysis.maestroPending || !analysis.maestroAlerts ? 'empty' : (analysis.thin.hasThinAreas ? 'warn' : 'good'),
          title: 'Zones fines',
          text: analysis.maestroPending
            ? 'Contrôle en cours via l’API de vérification.'
            : !analysis.maestroAlerts
              ? (analysis.maestroError || 'La vérification API n’a pas pu aboutir.')
              : analysis.thin.hasThinAreas
                ? `Des zones trop fines ont été détectées. Les traits très fins peuvent être fragiles ou manquer de netteté.`
                : 'Aucune zone trop fine notable n’a été détectée à cette taille.',
        },
        colors: {
          tone: analysis.maestroPending || !analysis.maestroAlerts ? 'empty' : (analysis.hasSpotColors ? 'warn' : 'good'),
          title: 'Colorimétrie',
          text: analysis.maestroPending
            ? 'Contrôle en cours via l’API de vérification.'
            : !analysis.maestroAlerts
              ? (analysis.maestroError || 'La vérification API n’a pas pu aboutir.')
              : analysis.hasSpotColors
                ? 'Des tons directs ont été détectés. Pour le DTF, il vaut mieux convertir le fichier en couleurs standard.'
                : 'Aucun ton direct problématique n’a été détecté.',
        },
      };
      return details[key];
    }

    function renderActions(analysis) {
      const actions = recommendedActions(analysis);
      refs.actionCount.textContent = `${actions.length} recommandation${actions.length > 1 ? 's' : ''}`;
      if (!actions.length) {
        refs.actions.innerHTML = analysis.maestroPending
          ? '<div class="lqc-placeholder">Contrôle en cours…</div>'
          : analysis.maestroError && !analysis.maestroAlerts
            ? '<div class="lqc-placeholder">Vérification incomplète. Réessayez pour obtenir les recommandations.</div>'
            : '<div class="lqc-placeholder">Aucune recommandation nécessaire à cette taille.</div>';
        return;
      }

      refs.actions.innerHTML = actions.map((action) => `
        <article class="lqc-action-card">
          <div class="lqc-action-topline">
            <strong>${escapeHtml(action.title)}</strong>
            <span class="lqc-importance-pill is-${escapeAttr(action.importanceTone)}">${escapeHtml(action.importance)}</span>
          </div>
          <p>${escapeHtml(action.text)}</p>
          <a class="lqc-btn ${action.primary ? 'lqc-btn-primary' : ''}" href="${escapeAttr(action.url)}">
            <span>${escapeHtml(action.cta)}</span>
          </a>
        </article>
      `).join('');
    }

    function recommendedActions(analysis) {
      const actions = [];

      if (!analysis.hasContent) {
        actions.push({
          title: 'Remplacer le fichier',
          text: 'Aucun logo exploitable n’a été détecté. Importez une version plus propre avant de préparer l’impression.',
          cta: 'Ouvrir l’éditeur de logo',
          url: serviceUrls.editor,
          importance: 'Haute',
          importanceTone: 'high',
          primary: true,
        });
        return actions;
      }

      if (analysis.hasWhitespace && shouldShowTrimmedMetric(analysis)) {
        actions.push({
          title: 'Supprimer les espaces vides',
          text: 'Les espaces vides autour du logo sont inutiles et peuvent créer des erreurs de placement à l’impression.',
          cta: 'Corriger dans l’éditeur',
          url: serviceUrls.editor,
          importance: 'Moyenne',
          importanceTone: 'medium',
          primary: false,
        });
      }

      if (analysis.maestroAlerts && analysis.hasResolutionIssue) {
        if (analysis.complex || analysis.dpi < DPI_MIN) {
          actions.push({
            title: 'Redessiner en vectoriel',
            text: `La résolution est trop faible à cette taille (${analysis.dpi} DPI). Un redessin vectoriel sécurise la netteté du logo.`,
            cta: 'Redessin par un graphiste',
            url: serviceUrls.vectorGraphic,
            importance: analysis.dpi < DPI_MIN ? 'Haute' : 'Moyenne',
            importanceTone: analysis.dpi < DPI_MIN ? 'high' : 'medium',
            primary: analysis.dpi < DPI_MIN,
          });
        } else {
          actions.push({
            title: 'Vectoriser le logo',
            text: `La résolution est trop faible à cette taille (${analysis.dpi} DPI). La vectorisation permet d’agrandir le logo sans perte de netteté.`,
            cta: 'Vectorisation IA',
            url: serviceUrls.vectorAi,
            importance: 'Moyenne',
            importanceTone: 'medium',
            primary: false,
          });
        }
      }

      if (analysis.maestroAlerts && analysis.hasSemiTransparency) {
        actions.push({
          title: 'Nettoyer la semi-transparence',
          text: 'Les pixels semi-transparents peuvent créer des bords flous ou imprévisibles. Il vaut mieux les rendre nets avant impression.',
          cta: 'Corriger dans l’éditeur',
          url: serviceUrls.editor,
          importance: 'Moyenne',
          importanceTone: 'medium',
          primary: false,
        });
      }

      if (analysis.maestroAlerts && analysis.hasBackground) {
        actions.push({
          title: 'Retirer le fond',
          text: 'Un fond opaque ou partiel peut s’imprimer comme un rectangle autour du logo.',
          cta: 'Suppression de fond IA',
          url: serviceUrls.removeBg,
          importance: analysis.backgroundStatus === 'solid' ? 'Haute' : 'Moyenne',
          importanceTone: analysis.backgroundStatus === 'solid' ? 'high' : 'medium',
          primary: analysis.backgroundStatus === 'solid',
        });
      }

      if (analysis.maestroAlerts && analysis.thin.hasThinAreas) {
        actions.push({
          title: 'Épaissir les zones fines',
          text: `Certaines zones semblent trop fines pour l’impression. Elles peuvent être fragiles ou manquer de netteté.`,
          cta: 'Corriger dans l’éditeur',
          url: serviceUrls.editor,
          importance: 'À vérifier',
          importanceTone: 'low',
          primary: false,
        });
      }

      if (analysis.maestroAlerts && analysis.hasSpotColors) {
        actions.push({
          title: 'Corriger la colorimétrie',
          text: 'Des tons directs ont été détectés. Pour le DTF, convertissez le fichier en couleurs standard avant impression.',
          cta: 'Corriger dans l’éditeur',
          url: serviceUrls.editor,
          importance: 'Moyenne',
          importanceTone: 'medium',
          primary: false,
        });
      }

      return actions;
    }

    function collectIssues(analysis) {
      const issues = [];
      const alerts = analysis.maestroAlerts;
      if (!analysis.hasContent) issues.push({ level: 'bad', text: 'Aucun logo détecté dans le fichier.' });
      if (alerts) {
        if (alerts.resolution) {
          issues.push({
            level: analysis.dpi < DPI_MIN ? 'bad' : 'warn',
            text: `Résolution trop faible : ${analysis.dpi} DPI.`,
          });
        }
        if (alerts.background) issues.push({ level: 'warn', text: 'Fond détecté par la vérification.' });
        if (alerts.semi) issues.push({ level: 'warn', text: 'Semi-transparence détectée.' });
        if (alerts.thin) issues.push({ level: 'warn', text: 'Zones fines détectées, à vérifier avant impression.' });
        if (alerts.colors) issues.push({ level: 'warn', text: 'Tons directs détectés.' });
      }
      if (analysis.hasContent && analysis.hasWhitespace && shouldShowTrimmedMetric(analysis)) {
        issues.push({ level: 'warn', text: `Espaces vides détectés : ${percent(analysis.whitespaceRatio)} du fichier.` });
      }
      return issues;
    }

    function buildReport(analysis) {
      const issues = collectIssues(analysis);
      const recommendations = recommendedActions(analysis).map((action) => ({
        title: action.title,
        text: action.text,
        importance: action.importance,
      }));
      const conclusion = issues.some((item) => item.level === 'bad')
        ? 'Fichier à corriger avant impression.'
        : issues.length
          ? 'Fichier imprimable avec points à vérifier.'
          : 'Fichier prêt pour impression.';
      /* Semi-transparence et zones fines viennent de la vérification serveur. */
      const validations = state.maestroValidations || {};
      const lines = [
        ['Taille du fichier originale', originalFileSizeLabel(analysis)],
        ['Taille du logo demandé', formatSize(analysis.printWidthCm, analysis.printHeightCm)],
        ['Type de fichier', analysis.isVector ? 'Vectoriel' : 'Pixel'],
        ['Résolution', analysis.isVector && !analysis.hasResolutionIssue ? 'Fichier vectoriel' : `${analysis.dpi} DPI`],
        ...maestroZoneLine('Semi-transparence', validations.mid_transparency),
        ['Fond', analysis.hasBackground ? (analysis.maestroAlerts ? 'Fond détecté' : backgroundLabel(analysis.backgroundStatus)) : 'Non'],
        ...maestroZoneLine('Zones fines', validations.thickness),
        ['Colorimétrie', analysis.hasSpotColors ? 'Tons directs' : 'OK'],
      ];
      if (shouldShowTrimmedMetric(analysis)) {
        lines.splice(2, 0, ['Dimensions du fichier sans espaces vides', analysis.boundsOriginal ? formatSize(analysis.trimmedWidthCm, analysis.trimmedHeightCm) : 'Logo non détecté']);
      }
      return {
        title: 'Rapport du vérificateur de logo',
        fileName: analysis.fileName,
        conclusion,
        lines,
        issues,
        recommendations,
      };
    }

    /* Les zones renvoyées par la vérification sont des images distantes : une
       image inaccessible ne doit pas priver le rapport de son verdict. */
    async function embedZoneImages(pdf, zones) {
      const images = await Promise.all(zones.map(async (zone) => {
        try {
          const response = await fetch(zone.url, { cache: 'no-store' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const bytes = new Uint8Array(await response.arrayBuffer());
          if (isPngBytes(bytes)) return { label: zone.label, image: await pdf.embedPng(bytes) };
          if (isJpegBytes(bytes)) return { label: zone.label, image: await pdf.embedJpg(bytes) };
          throw new Error('Format d’image non supporté');
        } catch (error) {
          console.warn('Image de zones non intégrée au rapport', zone.label, error);
          return null;
        }
      }));
      return images.filter(Boolean);
    }

    async function buildReportPdf() {
      const { PDFDocument, StandardFonts, rgb } = window.PDFLib;
      const pdf = await PDFDocument.create();
      const font = await pdf.embedFont(StandardFonts.Helvetica);
      const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
      const zoneImages = await embedZoneImages(pdf, maestroProblemZones());

      const pageSize = [595.28, 841.89];
      const margin = 48;
      const width = pageSize[0] - margin * 2;
      const top = pageSize[1] - 50;
      const bottom = 56;
      const ink = rgb(0.07, 0.09, 0.14);
      const soft = rgb(0.38, 0.42, 0.5);
      const accent = rgb(0.85, 0.2, 0.5);
      const good = rgb(0.1, 0.45, 0.22);

      let page = pdf.addPage(pageSize);
      let y = top;

      /* Le rapport tenait sur une seule page : au-delà, le contenu était dessiné
         sous le bord inférieur et devenait invisible. */
      function ensureRoom(needed) {
        if (y - needed >= bottom) return;
        page = pdf.addPage(pageSize);
        y = top;
      }

      function writeLine(text, size, usedFont, color, gap) {
        ensureRoom(size);
        drawText(page, text, margin, y, size, usedFont, color);
        y -= gap;
      }

      function writeParagraph(text, size, usedFont, color, gap) {
        wrapTextLines(text, usedFont, size, width).forEach((line) => {
          ensureRoom(size);
          drawText(page, line, margin, y, size, usedFont, color);
          y -= size + 4;
        });
        y -= gap;
      }

      writeLine(state.report.title, 20, bold, ink, 28);
      writeLine(`Fichier : ${state.report.fileName}`, 10, font, soft, 18);
      writeLine(`Date : ${new Date().toLocaleDateString('fr-FR')}`, 10, font, soft, 30);
      writeParagraph(state.report.conclusion, 14, bold, accent, 14);

      state.report.lines.forEach(([label, value]) => {
        ensureRoom(10);
        drawText(page, label, margin, y, 10, bold, ink);
        drawText(page, value, margin + 210, y, 10, font, ink);
        y -= 18;
      });

      y -= 12;
      writeLine('Erreurs et points à vérifier', 13, bold, ink, 20);

      if (!state.report.issues.length) {
        writeLine('Aucun point bloquant détecté.', 10, font, good, 22);
      } else {
        state.report.issues.forEach((issue) => {
          const color = issue.level === 'bad' ? rgb(0.72, 0.1, 0.1) : rgb(0.72, 0.38, 0.02);
          writeParagraph(`- ${issue.text}`, 10, font, color, 8);
        });
      }

      y -= 8;
      writeLine('Recommandations', 13, bold, ink, 20);

      if (!state.report.recommendations.length) {
        writeLine('Aucune recommandation nécessaire à cette taille.', 10, font, good, 0);
      } else {
        state.report.recommendations.forEach((recommendation) => {
          writeLine(`${recommendation.importance} - ${recommendation.title}`, 10, bold, ink, 15);
          writeParagraph(recommendation.text, 10, font, soft, 10);
        });
      }

      if (zoneImages.length) {
        y -= 14;
        writeLine('Zones détectées par la vérification', 13, bold, ink, 20);
        zoneImages.forEach((zone) => {
          const ratio = zone.image.height / zone.image.width;
          const drawHeight = Math.min(Math.min(width, zone.image.width) * ratio, 280);
          ensureRoom(drawHeight + 26);
          drawText(page, zone.label, margin, y, 10, bold, ink);
          y -= drawHeight + 12;
          page.drawImage(zone.image, { x: margin, y, width: drawHeight / ratio, height: drawHeight });
          y -= 20;
        });
      }

      return pdf.save();
    }

    async function downloadReport() {
      if (!state.report) return;
      if (!window.PDFLib) {
        showToast('Export PDF indisponible');
        return;
      }
      if (!(await confirmDownloadCredit('rapport PDF'))) return;

      /* Le PDF est généré avant le débit : un échec de génération ne doit pas
         consommer un crédit. */
      let bytes;
      setBusy(true);
      try {
        bytes = await buildReportPdf();
      } catch (error) {
        console.error(error);
        showToast('Rapport impossible');
        return;
      } finally {
        setBusy(false);
      }

      if (!(await consumeDownloadCredit())) return;

      downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `${baseName(state.file.name)}-rapport-qualite.pdf`);
      showToast('Rapport téléchargé');
      syncCreditsAfterDownload();
    }

    function renderPreview(analysis = null) {
      const rect = previewRectForAnalysis(analysis);
      if (refs.previewShell) {
        refs.previewShell.style.setProperty('--lqc-preview-ratio', `${rect.w} / ${rect.h}`);
      }
      const maxW = Math.max(80, refs.previewCanvas.parentElement.clientWidth - 28);
      const maxH = Math.max(80, refs.previewCanvas.parentElement.clientHeight - 28);
      const scale = Math.max(0.01, Math.min(maxW / rect.w, maxH / rect.h));
      refs.previewCanvas.width = Math.max(1, Math.round(rect.w * scale));
      refs.previewCanvas.height = Math.max(1, Math.round(rect.h * scale));
      previewCtx.clearRect(0, 0, refs.previewCanvas.width, refs.previewCanvas.height);
      previewCtx.imageSmoothingEnabled = true;
      previewCtx.imageSmoothingQuality = 'high';
      previewCtx.drawImage(canvas, rect.x, rect.y, rect.w, rect.h, 0, 0, refs.previewCanvas.width, refs.previewCanvas.height);
      refs.emptyState.hidden = true;
    }

    function renderThumb(analysis = null) {
      const rect = previewRectForAnalysis(analysis, 0.08);
      refs.thumbCanvas.width = 54;
      refs.thumbCanvas.height = 54;
      thumbCtx.clearRect(0, 0, 54, 54);
      const scale = Math.min(54 / rect.w, 54 / rect.h);
      const w = rect.w * scale;
      const h = rect.h * scale;
      thumbCtx.drawImage(canvas, rect.x, rect.y, rect.w, rect.h, (54 - w) / 2, (54 - h) / 2, w, h);
    }

    function previewRectForAnalysis(analysis, paddingRatio = 0.1) {
      if (!analysis || !analysis.boundsCanvas) return { x: 0, y: 0, w: canvas.width, h: canvas.height };
      const bounds = analysis.boundsCanvas;
      const padding = Math.max(6, Math.round(Math.max(bounds.w, bounds.h) * paddingRatio));
      const x = Math.max(0, Math.floor(bounds.x - padding));
      const y = Math.max(0, Math.floor(bounds.y - padding));
      const right = Math.min(canvas.width, Math.ceil(bounds.x + bounds.w + padding));
      const bottom = Math.min(canvas.height, Math.ceil(bounds.y + bounds.h + padding));
      return {
        x,
        y,
        w: Math.max(1, right - x),
        h: Math.max(1, bottom - y),
      };
    }

    function updateFileCard(meta) {
      refs.fileCard.hidden = false;
      refs.fileName.textContent = state.file.name;
      refs.fileMeta.textContent = meta;
    }

    function setPrintWidth(value) {
      if (!(value > 0)) return;
      const nextHeight = value / Math.max(0.001, state.sourceRatio || 1);
      const unchanged = Math.abs(value - state.printWidthCm) < 0.001
        && Math.abs(nextHeight - state.printHeightCm) < 0.001;
      state.printWidthCm = value;
      state.printHeightCm = nextHeight;
      analyzeAndRender('width');
      if (!unchanged) scheduleMaestroRefresh();
    }

    function setPrintHeight(value) {
      if (!(value > 0)) return;
      const nextWidth = value * Math.max(0.001, state.sourceRatio || 1);
      const unchanged = Math.abs(value - state.printHeightCm) < 0.001
        && Math.abs(nextWidth - state.printWidthCm) < 0.001;
      state.printHeightCm = value;
      state.printWidthCm = nextWidth;
      analyzeAndRender('height');
      if (!unchanged) scheduleMaestroRefresh();
    }

    function resetPrintSize(runAnalysis = true) {
      if (!state.file) return;
      if (state.isVector) {
        state.printWidthCm = state.pageWidthCm || pxToCm(state.originalWidth);
        state.printHeightCm = state.pageHeightCm || pxToCm(state.originalHeight);
      } else {
        state.printWidthCm = pxToCm(state.originalWidth);
        state.printHeightCm = pxToCm(state.originalHeight);
      }
      if (runAnalysis) {
        analyzeAndRender();
        scheduleMaestroRefresh();
      } else syncSizeInputs();
    }

    function syncSizeInputs(preserveAxis = '') {
      if (preserveAxis !== 'width') refs.printWidth.value = formatNumber(state.printWidthCm);
      if (preserveAxis !== 'height') refs.printHeight.value = formatNumber(state.printHeightCm);
    }

    function reset(clearInput = true) {
      resetMaestroState();
      state.file = null;
      state.fileKind = '';
      state.isVector = false;
      state.originalWidth = 0;
      state.originalHeight = 0;
      state.pageWidthCm = 0;
      state.pageHeightCm = 0;
      state.renderScale = 1;
      state.printWidthCm = 0;
      state.printHeightCm = 0;
      state.sourceRatio = 1;
      state.imageReady = false;
      state.analysis = null;
      state.report = null;
      canvas.width = 0;
      canvas.height = 0;
      if (refs.previewShell) refs.previewShell.style.removeProperty('--lqc-preview-ratio');
      refs.fileCard.hidden = true;
      refs.emptyState.hidden = false;
      refs.previewCanvas.width = 1;
      refs.previewCanvas.height = 1;
      refs.printWidth.value = '';
      refs.printHeight.value = '';
      refs.printWidth.disabled = true;
      refs.printHeight.disabled = true;
      if (refs.resetSize) refs.resetSize.disabled = true;
      refs.reset.disabled = true;
      refs.report.disabled = true;
      updateReportButton();
      setQualityBusy(false);
      setScore('En attente', '––/100', 'empty');
      refs.overall.textContent = 'En attente';
      if (refs.retryMaestro) refs.retryMaestro.hidden = true;
      Object.keys(metricEls).forEach((key) => setMetric(key, 'En attente', 'empty'));
      resetMetricDetail();
      refs.actionCount.textContent = '0 recommandation';
      refs.actions.innerHTML = '<div class="lqc-placeholder">Les recommandations apparaîtront après analyse.</div>';
      if (clearInput) refs.input.value = '';
    }

    function closeChecker() {
      const launcher = root.__logoQualityCheckerLauncher;
      if (launcher) {
        root.remove();
        if (!document.querySelector('[data-logo-quality-checker]')) {
          document.body.classList.remove('lqc-checker-open');
        }
        launcher.setAttribute('aria-expanded', 'false');
        launcher.focus();
        return;
      }

      const closeUrl = root.dataset.closeUrl || '/';
      document.body.classList.remove('lqc-checker-open');
      window.location.href = closeUrl;
    }

    function setQualityBusy(active) {
      const checking = Boolean(active);
      if (refs.diagnosticSection) refs.diagnosticSection.classList.toggle('is-checking', checking);
      if (refs.qualityBusy) refs.qualityBusy.hidden = !checking;
      if (refs.scoreSpinner) refs.scoreSpinner.hidden = !checking;
      if (refs.scorePill) refs.scorePill.classList.toggle('is-checking', checking);
    }

    function setScore(label, value, tone) {
      refs.scoreLabel.textContent = label;
      refs.scoreValue.textContent = value;
      refs.scorePill.title = tone === 'empty' ? '' : `${label} - score qualité ${value}`;
      refs.scorePill.classList.remove('is-good', 'is-warn', 'is-bad', 'is-empty');
      refs.scorePill.classList.add(`is-${tone}`);
    }

    function setMetric(key, value, tone) {
      const metric = metricEls[key];
      if (!metric) return;
      metric.value.textContent = value;
      metric.item.classList.remove('is-good', 'is-warn', 'is-bad', 'is-empty');
      metric.item.classList.add(`is-${tone}`);
      if (metric.toggle) metric.toggle.disabled = tone === 'empty';
      renderMetricDetail(key);
      setMetricOpen(key, tone === 'warn');
    }

    function setMetricVisible(key, visible) {
      const metric = metricEls[key];
      if (!metric) return;
      metric.item.hidden = !visible;
      if (!visible) setMetricOpen(key, false);
    }

    function resetMetricDetail() {
      Object.keys(metricEls).forEach((key) => {
        const metric = metricEls[key];
        setMetricOpen(key, false);
        if (metric.detailText) metric.detailText.textContent = '';
      });
    }

    function renderMetricDetail(key) {
      if (!state.analysis) return;
      const metric = metricEls[key];
      const detail = metricDetail(key, state.analysis);
      if (!metric || !detail) return;
      if (metric.detailText) metric.detailText.textContent = detail.text;
    }

    function setMetricOpen(key, open) {
      const metric = metricEls[key];
      if (!metric) return;
      metric.item.classList.toggle('is-open', open);
      metric.item.setAttribute('aria-expanded', String(open));
      if (metric.toggle) metric.toggle.setAttribute('aria-expanded', String(open));
    }

    function shouldShowTrimmedMetric(analysis) {
      if (!analysis || !analysis.boundsOriginal) return true;
      const widthDelta = Math.abs((analysis.printWidthCm || 0) - (analysis.trimmedWidthCm || 0));
      const heightDelta = Math.abs((analysis.printHeightCm || 0) - (analysis.trimmedHeightCm || 0));
      return analysis.hasWhitespace || widthDelta >= 0.05 || heightDelta >= 0.05;
    }

    function setImportDragging(dragging) {
      [refs.dropzone, refs.preview, refs.emptyState].forEach((element) => {
        element?.classList.toggle('is-dragging', dragging);
      });
    }

    /* Classe distincte de `is-dragging` : l'analyse en cours ne doit pas
       ressembler à un survol de dépôt. */
    function setBusy(busy) {
      refs.dropzone.classList.toggle('is-busy', busy);
    }

    function showToast(message) {
      refs.toast.textContent = message;
      refs.toast.classList.add('is-visible');
      clearTimeout(showToast.timer);
      showToast.timer = setTimeout(() => refs.toast.classList.remove('is-visible'), 2600);
    }

    function effectiveDpi() {
      const widthIn = state.printWidthCm / CM_PER_INCH;
      const heightIn = state.printHeightCm / CM_PER_INCH;
      const widthDpi = widthIn > 0 ? state.originalWidth / widthIn : 0;
      const heightDpi = heightIn > 0 ? state.originalHeight / heightIn : 0;
      return Math.max(1, Math.round(Math.min(widthDpi || heightDpi, heightDpi || widthDpi)));
    }

    function pxToCurrentCm(px, axis) {
      if (axis === 'w') return (px / state.originalWidth) * state.printWidthCm;
      return (px / state.originalHeight) * state.printHeightCm;
    }
  }

  function analyzeEdges(data, width, height) {
    let samples = 0;
    let transparent = 0;
    let opaque = 0;
    const maxSamples = 80;
    const add = (x, y) => {
      const idx = (y * width + x) * 4;
      const alpha = data[idx + 3];
      samples += 1;
      if (alpha < 245) transparent += 1;
      if (alpha > 245) opaque += 1;
    };
    for (let i = 0; i < maxSamples; i += 1) {
      const x = Math.round((width - 1) * (i / (maxSamples - 1)));
      const y = Math.round((height - 1) * (i / (maxSamples - 1)));
      add(x, 0);
      add(x, height - 1);
      add(0, y);
      add(width - 1, y);
    }
    return { samples, transparent, opaque };
  }

  function edgeAverageColor(data, width, height) {
    const samples = [];
    const add = (x, y) => {
      const idx = (y * width + x) * 4;
      if (data[idx + 3] < 8) return;
      samples.push([data[idx], data[idx + 1], data[idx + 2]]);
    };
    const maxSamples = 60;
    for (let i = 0; i < maxSamples; i += 1) {
      const x = Math.round((width - 1) * (i / (maxSamples - 1)));
      const y = Math.round((height - 1) * (i / (maxSamples - 1)));
      add(x, 0);
      add(x, height - 1);
      add(0, y);
      add(width - 1, y);
    }
    if (!samples.length) return [255, 255, 255];
    const sum = samples.reduce((acc, item) => [acc[0] + item[0], acc[1] + item[1], acc[2] + item[2]], [0, 0, 0]);
    return sum.map((value) => Math.round(value / samples.length));
  }

  function colorDistanceAt(data, idx, rgb) {
    const dr = data[idx] - rgb[0];
    const dg = data[idx + 1] - rgb[1];
    const db = data[idx + 2] - rgb[2];
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  function readSvgGeometry(svg) {
    const attrs = String(svg).match(/<svg\b([^>]*)>/i)?.[1] || '';
    const width = parseSvgLength(getAttr(attrs, 'width'));
    const height = parseSvgLength(getAttr(attrs, 'height'));
    const viewBox = getAttr(attrs, 'viewBox');
    const vb = viewBox ? viewBox.trim().split(/[\s,]+/).map(Number) : [];
    const vbWidth = Number.isFinite(vb[2]) ? vb[2] : 0;
    const vbHeight = Number.isFinite(vb[3]) ? vb[3] : 0;
    return {
      width: width.px || vbWidth || 1000,
      height: height.px || vbHeight || 1000,
      widthCm: width.cm || 0,
      heightCm: height.cm || 0,
    };
  }

  function getAttr(attrs, name) {
    const match = attrs.match(new RegExp(`${name}=["']([^"']+)["']`, 'i'));
    return match ? match[1] : '';
  }

  function parseSvgLength(value) {
    const raw = String(value || '').trim();
    const match = raw.match(/^([\d.]+)\s*(px|cm|mm|in|pt)?$/i);
    if (!match) return { px: 0, cm: 0 };
    const number = Number(match[1]);
    const unit = (match[2] || 'px').toLowerCase();
    if (!Number.isFinite(number)) return { px: 0, cm: 0 };
    if (unit === 'cm') return { px: number * DPI_PERFECT / CM_PER_INCH, cm: number };
    if (unit === 'mm') return { px: number * DPI_PERFECT / 25.4, cm: number / 10 };
    if (unit === 'in') return { px: number * DPI_PERFECT, cm: number * CM_PER_INCH };
    if (unit === 'pt') return { px: number * DPI_PERFECT / 72, cm: number / 72 * CM_PER_INCH };
    return { px: number, cm: 0 };
  }

  /* Helvetica standard n'encode que le jeu WinAnsi : un nom de fichier avec des
     caractères hors de ce jeu faisait échouer tout l'export. */
  function pdfSafe(value) {
    return String(value ?? '')
      .normalize('NFC')
      .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
      .replace(/[\u201C\u201D\u201E\u2033]/g, '"')
      .replace(/[\u2010-\u2015]/g, '-')
      .replace(/\u2026/g, '...')
      .replace(/[\u00A0\u2007\u202F\u2009\u200A]/g, ' ')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[^\x20-\x7E\u00A0-\u00FF\u20AC]/g, '?');
  }

  function drawText(page, text, x, y, size, font, color) {
    page.drawText(pdfSafe(text), { x, y, size, font, color });
  }

  function wrapTextLines(text, font, size, maxWidth) {
    const words = pdfSafe(text).split(' ').filter(Boolean);
    const lines = [];
    let line = '';
    words.forEach((word) => {
      const test = line ? `${line} ${word}` : word;
      if (line && font.widthOfTextAtSize(test, size) > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    });
    if (line) lines.push(line);
    return lines.length ? lines : [''];
  }

  function isPngBytes(bytes) {
    return bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
  }

  function isJpegBytes(bytes) {
    return bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  }

  function downloadBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    window.setTimeout(() => {
      link.remove();
      URL.revokeObjectURL(url);
    }, 1200);
  }

  function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
      reader.readAsDataURL(file);
    });
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Image illisible'));
      image.src = src;
    });
  }

  function extensionOf(name) {
    return String(name || '').split('.').pop().toLowerCase();
  }

  function pxToCm(px) {
    return (px / DPI_PERFECT) * CM_PER_INCH;
  }

  function ptToCm(pt) {
    return (pt / 72) * CM_PER_INCH;
  }

  function scaleRect(rect, factor) {
    return {
      x: rect.x * factor,
      y: rect.y * factor,
      w: rect.w * factor,
      h: rect.h * factor,
    };
  }

  function formatSize(widthCm, heightCm) {
    return `${formatNumber(widthCm)} x ${formatNumber(heightCm)} cm`;
  }

  function originalFileSizeLabel(analysis) {
    if (analysis.isVector && analysis.pageWidthCm > 0 && analysis.pageHeightCm > 0) {
      return `${formatSize(analysis.pageWidthCm, analysis.pageHeightCm)} (${analysis.originalWidth} x ${analysis.originalHeight} px de contrôle)`;
    }
    if (analysis.isVector) {
      return `Vectoriel (${analysis.originalWidth} x ${analysis.originalHeight} px de contrôle)`;
    }
    return `${analysis.originalWidth} x ${analysis.originalHeight} px`;
  }

  function formatNumber(value) {
    return (Math.round((Number(value) || 0) * 10) / 10).toFixed(1);
  }

  function percent(value) {
    return `${Math.round((Number(value) || 0) * 100)}%`;
  }

  function parseCreditBalance(value) {
    if (value === null || typeof value === 'undefined' || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : null;
    if (typeof value === 'string') {
      const match = value.replace(',', '.').match(/-?\d+(\.\d+)?/);
      if (!match) return null;
      const parsed = Number(match[0]);
      return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : null;
    }
    if (typeof value === 'object') {
      return parseCreditBalance(value.remaining ?? value.balance ?? value.credits ?? value.value ?? value.data);
    }
    return null;
  }

  function parseFlag(value) {
    if (value === true || value === 1) return true;
    if (typeof value !== 'string') return false;
    return ['true', '1', 'yes', 'oui'].includes(value.trim().toLowerCase());
  }

  function dpiTone(dpi) {
    if (dpi >= DPI_PERFECT) return 'good';
    if (dpi >= DPI_MIN) return 'warn';
    return 'bad';
  }

  function backgroundLabel(status) {
    if (status === 'none') return 'Non';
    if (status === 'partial') return 'Partiel';
    return 'Oui, fond opaque';
  }

  function baseName(name) {
    return String(name || 'logo')
      .replace(/\.[^.]+$/, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9_-]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'logo';
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
  }

  function escapeAttr(value) {
    return escapeHtml(value);
  }
})();
